var gotThrottled = false

/**
 * Will only allow the function to be called more than once every {limit} ms
 *
 * @param {*} func
 * @param {*} limit
 * @param {*} applyLast
 * @returns
 */
export function throttle(func, limit, applyLast = false) {
    let inThrottle
    let lastArgs
    let lastContext

    return function (...args) {
        const context = this

        // Capture relevant parts of the event if it exists
        if (args[0] instanceof Event) {
            const event = args[0]
            args[0] = {
                currentTarget: event.currentTarget,
                scrollTop: event.currentTarget?.scrollTop,
            }
        }

        if (!inThrottle) {
            func.apply(context, args)
            inThrottle = true
            setTimeout(() => {
                inThrottle = false
                // Apply the function with the last saved arguments if throttled
                if (lastArgs && applyLast) {
                    func.apply(lastContext, lastArgs)
                }
                lastArgs = null
                lastContext = null
            }, limit)
        } else {
            lastArgs = args // Save the arguments for the last invocation
            lastContext = context // Save the context for the last invocation
        }
    }
}

/**
 *  Will only allow the function to be called after the debounce function has not been called again for the delay time
 *
 * @param {*} func
 * @param {*} delay
 * @returns
 */
export function debounce(func, delay = 500) {
    let timer
    return function (...args) {
        clearTimeout(timer)
        timer = setTimeout(() => {
            func.apply(this, args)
        }, delay)
    }
}

/**
 * Will start a timer, and when the trigger is called measure if the delay is met, then call the callback
 * Otherwise start a timeout with the remaining time and then call the callback after the delay
 */
export function minimumLoadingTime(func, delay) {
    // Start the timer
    let start = Date.now()

    // Return a callbackfunction that when triggered checks the remaining time and calls the callback or sets a timeout
    return function (...args) {
        const now = Date.now()
        const timePassed = now - start
        if (timePassed >= delay) {
            func.apply(this, args)
        } else {
            setTimeout(() => {
                func.apply(this, args)
            }, delay - timePassed)
        }
    }
}

/**
 * Will check {remainingTimes} times if the image is loaded
 * When loaded or after {remainingTimes} times, it will return a boolean
 * The boolean will indicate if the image is loaded or not, when the promise is resolved then the image has loaded or we wont wait for it
 *
 * @param {number} [remainingTimes] - The amount of times to check if the image is loaded
 * @param {number} [timeout] - The amount of time to wait between checks
 * @param {function} fetchImage - A function that returns the image that we can check if it is loaded
 * @returns boolean - True if the image is had an error, false if not.
 *
 */
export function checkIfImageLoaded(
    remainingTimes = 0,
    timeout = 100,
    fetchImage
) {
    return new Promise((resolve) => {
        const image = fetchImage()
        if (!image) {
            resolve(true)
            return
        }

        if (remainingTimes <= 0) {
            // If we have tried N amount of times and the image is still not loaded
            resolve(true)
            return
        }

        setTimeout(() => {
            if (!image.complete) {
                resolve(
                    checkIfImageLoaded(remainingTimes - 1, timeout, fetchImage)
                )
            } else {
                // If natualHeight or naturalWidth is 0, the image is not loaded or link was broken
                resolve(image.naturalHeight == 0 || image.naturalWidth == 0)
            }
        }, timeout)
    })
}

export function upperCaseFirst(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
}

export function isValidUrl(string) {
    if (string == null || string.length < 4) {
        return false
    }
    let isValidUrl =
        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/
    return isValidUrl.test(string)
}

/**
 * This checks if the value is an array or like an array
 * This is needed since vue sometime wraps values in a Proxy like this Proxy (Array) which makes
 * it not evaluate as an array
 */
export function isArrayLike(value) {
    return (
        // Check if its an array
        Array.isArray(value) ||
        // Check if its an object with a length property
        (typeof value === 'object' &&
            value !== null &&
            'length' in value &&
            typeof value.length === 'number' &&
            (value.length === 0 ||
                (value.length > 0 && value.length - 1 in value)))
    )
}

/**
 * This function will take an image and return a base64 string of the image
 * If the image is a url, it will fetch the image and then return the base64 string
 * If the image is a file, it will read the file and then return the base64 string
 *
 * @param {File | string} image - The image to process, can be either a File or a url
 * @param {number | null} [newWidth] - The new width of the image
 * @param {number | null} [newHeight] - The new height of the image
 * @param {number} [quality] - The quality of the image (0-1)
 * @returns {Promise<string>} - A promise that resolves to a base64 string of the image
 */
export function getImageAsBase64({
    image,
    newWidth = null,
    newHeight = null,
    quality = 1,
}) {
    return new Promise((resolve, reject) => {
        const processImage = (img) => {
            // Create a canvas element
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')

            // Calculate scale factor to fit within specified dimensions while preserving aspect ratio
            let scaleFactor = 1
            let width = img.width
            let height = img.height

            // If there are provided dimeonsions and the image is larger than the newWidth or newHeight, set the image to those sizes
            if (
                newWidth &&
                newHeight &&
                width > newWidth &&
                height > newHeight
            ) {
                scaleFactor = Math.min(newWidth / width, newHeight / height)
                // If only width is provided and the image is larger than the newWidth, set the image to the newWidth
            } else if (newWidth && width > newWidth) {
                scaleFactor = newWidth / width
                // If only height is provided and the image is larger than the newHeight, set the image to the newHeight
            } else if (newHeight && height > newHeight) {
                scaleFactor = newHeight / height
            }

            // Apply scale factor to both dimensions
            width *= scaleFactor
            height *= scaleFactor

            canvas.width = width
            canvas.height = height

            // Draw the resized image onto the canvas
            ctx.drawImage(img, 0, 0, width, height)

            // Convert the canvas back to a data URL
            resolve(canvas.toDataURL('image/png', quality))
        }

        // Check if image is a string (URL) or File object
        const isUrl = typeof image === 'string'

        if (isUrl) {
            // Check the the image is a valid url
            if (!isValidUrl(image)) {
                reject(new Error('Invalid URL'))
            }

            const img = new Image()
            img.crossOrigin = 'anonymous'
            img.onload = () => processImage(img)
            img.onerror = reject
            img.src = image
        } else {
            const reader = new FileReader()
            reader.onload = (e) => {
                const img = new Image()
                img.onload = () => processImage(img)
                img.src = e.target.result
            }
            reader.onerror = reject
            reader.readAsDataURL(image)
        }
    })
}

export function isElementInView(el, container) {
    if (el) {
        const elTop = el.offsetTop
        const elBottom = elTop + el.clientHeight
        const containerTop = container.scrollTop
        const containerBottom = containerTop + container.clientHeight

        // The element is fully visible in the container
        return elTop >= containerTop && elBottom <= containerBottom
    }
    return false
}

export function isDifferent(value1, value2) {
    if (value1 === value2) {
        return false // Equal for simple values (e.g., numbers, strings, booleans)
    }

    // Check for array difference
    if (Array.isArray(value1) && Array.isArray(value2)) {
        if (value1.length !== value2.length) {
            return true
        }
        return value1.some((item, index) => isDifferent(item, value2[index]))
    }

    // Check for object difference
    if (isObject(value1) && isObject(value2)) {
        const keys1 = Object.keys(value1)
        const keys2 = Object.keys(value2)

        if (keys1.length !== keys2.length) {
            return true
        }

        return keys1.some(
            (key) =>
                !keys2.includes(key) || isDifferent(value1[key], value2[key])
        )
    }

    // Fallback for non-array, non-object mismatches
    return true
}

// Helper function to check if a value is an object
function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function isInputKey(event) {
    // Ignore if any modifier keys are pressed
    if (event.ctrlKey || event.altKey || event.metaKey) {
        return false
    }

    // Ignore navigation keys
    const nonInputKeys = [
        'Tab',
        'Enter',
        'Shift',
        'Control',
        'Alt',
        'CapsLock',
        'Meta',
        'Escape',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'PageUp',
        'PageDown',
        'Delete',
        'Backspace',
    ]

    return !nonInputKeys.includes(event.key)
}
